/* eslint-disable no-param-reassign */
// The page's smaller motion systems: the scroll reveal, the comparison table's marks landing one by one,
// and the counters that run from 0 to their figure. Each takes the element it animates, owns its own
// observers and returns its teardown. Everything degrades to the markup as it ships: without
// IntersectionObserver, or under reduced motion, nothing is ever hidden or zeroed.
import { clamp } from './hero-sim';

const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** [data-reveal] elements and the direct children of [data-reveal-group] (staggered through --reveal-i) fade in once. */
export function initReveal(scope: HTMLElement): () => void {
  const items = Array.from(scope.querySelectorAll<HTMLElement>('[data-reveal]'));
  scope.querySelectorAll<HTMLElement>('[data-reveal-group]').forEach((group) => {
    Array.from(group.children).forEach((child, index) => {
      if (!(child instanceof HTMLElement)) return;
      if (!child.style.getPropertyValue('--reveal-i')) child.style.setProperty('--reveal-i', String(index));
      items.push(child);
    });
  });
  const show = (el: HTMLElement) => el.classList.add('is-visible');
  if (!items.length) return () => undefined;
  if (typeof IntersectionObserver !== 'function' || reducedMotion()) {
    items.forEach(show);
    return () => undefined;
  }
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        show(entry.target as HTMLElement);
        observer.unobserve(entry.target);
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -12% 0px' }
  );
  items.forEach((el) => observer.observe(el));
  /* one delayed sweep: anything the observer has not reported (a zero-height slot) is shown */
  const sweep = window.setTimeout(() => {
    items.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) show(el);
    });
  }, 900);
  return () => {
    observer.disconnect();
    window.clearTimeout(sweep);
  };
}

/* Comparison table: row by row, NoPing first, then VPN comum and Sem otimização (110 ms apart; rows 230 ms
   apart). Each row starts once it is in view, queued behind the previous one. It replays after the table
   has fully left the screen; on a phone (stacked cards) a card that has played stays played. */
export function initComparisonEntrance(table: HTMLElement): () => void {
  if (typeof IntersectionObserver !== 'function' || reducedMotion()) return () => undefined;
  const rows = Array.from(table.querySelectorAll<HTMLElement>('tbody tr'));
  if (!rows.length) return () => undefined;
  const ROW = 230;
  const CELL = 110;
  let queueAt = 0;
  let timers: number[] = [];
  let pending: HTMLElement[] = [];
  let armed = false;
  let everArmed = false;
  const stacked = () => window.getComputedStyle(table).display === 'block';
  function stagger() {
    const cell = stacked() ? 95 : CELL;
    rows.forEach((row) => {
      row.querySelectorAll<HTMLElement>('td').forEach((td, index) => td.style.setProperty('--d', `${index * cell}ms`));
    });
  }
  stagger();
  function play(row: HTMLElement) {
    if (!armed) return;
    if (document.hidden) {
      pending.push(row);
      return;
    }
    row.classList.add('is-play');
  }
  function queue(row: HTMLElement) {
    if (row.getAttribute('data-cmp-q')) return;
    row.setAttribute('data-cmp-q', '1');
    const now = Date.now();
    const at = Math.max(now, queueAt);
    queueAt = at + (stacked() ? 150 : ROW);
    timers.push(window.setTimeout(() => play(row), at - now));
  }
  function arm() {
    timers.forEach((id) => window.clearTimeout(id));
    timers = [];
    pending = [];
    queueAt = 0;
    rows.forEach((row) => {
      row.classList.remove('is-play');
      row.removeAttribute('data-cmp-q');
    });
    armed = true;
    everArmed = true;
    stagger();
    table.classList.add('is-armed');
  }
  const rowObserver = new IntersectionObserver(
    (entries) => {
      if (!armed) return;
      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio >= (stacked() ? 0.2 : 0.35)) queue(entry.target as HTMLElement);
      });
    },
    { threshold: [0.2, 0.35], rootMargin: '0px 0px -6% 0px' }
  );
  const tableObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting && !(everArmed && stacked())) arm();
      });
    },
    { threshold: 0 }
  );
  tableObserver.observe(table);
  rows.forEach((row) => rowObserver.observe(row));
  const onVisibility = () => {
    if (document.hidden || !pending.length) return;
    const waiting = pending;
    pending = [];
    queueAt = 0;
    waiting.forEach((row) => {
      row.removeAttribute('data-cmp-q');
      queue(row);
    });
  };
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    timers.forEach((id) => window.clearTimeout(id));
    rowObserver.disconnect();
    tableObserver.disconnect();
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

interface Counter {
  el: HTMLElement;
  to: number;
  fin: string;
  host: HTMLElement;
  state: 'final' | 'zero' | 'run' | 'done';
  raf: number;
  w: number;
  s: number;
}

/* Counters: 0 → final every time a figure comes into view. One observer arms a figure (back to 0) while it
   is still a screen away, a second one starts the count once it is in view; far off screen it rests on its
   final value. Every figure box is pinned to the width of its FINAL text and a running string within 10% of
   it is fitted into that box (scaleX), so nothing beside it moves when the last frame lands. */
export function initCounters(scope: HTMLElement, format: (value: number) => string): () => void {
  const groups = Array.from(scope.querySelectorAll<HTMLElement>('[data-counters]'));
  if (!groups.length || typeof IntersectionObserver !== 'function' || reducedMotion()) return () => undefined;
  const DUR = 2000;
  const items: Counter[] = [];
  groups.forEach((group) => {
    group.querySelectorAll<HTMLElement>('.cnt').forEach((el) => {
      items.push({ el, to: Number(el.getAttribute('data-count')), fin: el.textContent || '', host: el.closest('li') || el, state: 'final', raf: 0, w: 0, s: 1 });
    });
  });
  function fit(counter: Counter) {
    counter.el.style.width = '';
    counter.el.style.transform = '';
    counter.s = 1;
    const current = counter.el.textContent;
    counter.el.textContent = counter.fin;
    counter.w = counter.el.getBoundingClientRect().width;
    counter.el.style.width = `${counter.w.toFixed(2)}px`;
    counter.el.textContent = current;
  }
  function textWidth(counter: Counter) {
    const range = document.createRange();
    range.selectNodeContents(counter.el);
    return range.getBoundingClientRect().width / (counter.s || 1);
  }
  function paint(counter: Counter, q: number) {
    const txt = q >= 1 ? counter.fin : format(Math.round(counter.to * q));
    if (counter.el.textContent !== txt) counter.el.textContent = txt;
    let s = 1;
    let tw = 0;
    if (q < 1) {
      tw = textWidth(counter);
      if (tw > counter.w * 0.9) s = counter.w / tw;
    }
    if (s !== counter.s) {
      counter.s = s;
      counter.el.style.transformOrigin = tw > counter.w ? '0 50%' : '100% 50%';
      counter.el.style.transform = s !== 1 ? `scaleX(${s.toFixed(4)})` : '';
    }
    if (q >= 1) counter.host.style.removeProperty('--p');
    else counter.host.style.setProperty('--p', q.toFixed(3));
  }
  function toFinal(counter: Counter) {
    if (counter.raf) cancelAnimationFrame(counter.raf);
    counter.raf = 0;
    paint(counter, 1);
    counter.state = 'final';
  }
  function arm(counter: Counter) {
    if (counter.state !== 'final') return;
    if (!counter.w) fit(counter);
    paint(counter, 0);
    counter.state = 'zero';
  }
  function run(counter: Counter, delay: number) {
    if (counter.state === 'final') arm(counter);
    if (counter.state !== 'zero') return;
    counter.state = 'run';
    let t0 = 0;
    const frame = (now: number) => {
      counter.raf = 0;
      if (!t0) t0 = now + delay;
      const p = clamp((now - t0) / DUR, 0, 1);
      paint(counter, 1 - (1 - p) ** 3);
      if (p < 1) counter.raf = requestAnimationFrame(frame);
      else counter.state = 'done';
    };
    counter.raf = requestAnimationFrame(frame);
  }
  items.forEach(fit);
  const byHost = (el: Element) => items.filter((counter) => counter.host === el);
  const near = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        byHost(entry.target).forEach((counter) => (entry.isIntersecting ? arm(counter) : toFinal(counter)));
      });
    },
    { rootMargin: '100% 0px 100% 0px', threshold: 0 }
  );
  const seen = new IntersectionObserver(
    (entries) => {
      let order = 0;
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const delay = order * 90;
        order += 1;
        byHost(entry.target).forEach((counter) => run(counter, delay));
      });
    },
    { threshold: 0.6 }
  );
  items.forEach((counter) => {
    near.observe(counter.host);
    seen.observe(counter.host);
  });
  /* the boxes are pinned in px: re-measure when the type size can change */
  let resizeTimer = 0;
  const refit = () =>
    items.forEach((counter) => {
      if (counter.state === 'final' || counter.state === 'done') fit(counter);
    });
  const onResize = () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(refit, 150);
  };
  window.addEventListener('resize', onResize);
  let alive = true;
  document.fonts?.ready.then(() => {
    if (alive) refit();
  });
  return () => {
    alive = false;
    near.disconnect();
    seen.disconnect();
    window.removeEventListener('resize', onResize);
    window.clearTimeout(resizeTimer);
    items.forEach(toFinal);
  };
}
